import React from 'react';
import PropTypes from 'prop-types';
import { Drawer, Button, Row, Col, Alert, Typography, Card } from 'antd';
import { PrimaryButton, TimeWithTimeZone } from '@common/components';
import { Colors } from '@common/tokens';
import { humanize } from '@common/services';
import { DO_NOT_REHIRE_QUERY } from '@working-experience/graphql';
import { useQuery } from '@common/hooks';

const { Text } = Typography;

const LEVEL = {
  organization: 'All Units in',
  organization_business: 'All Units in',
  organization_unit: 'Only',
};

export function DoNotRehireDrawer({
  visible,
  onClose,
  handleSubmit,
  profile,
  supplierId,
  staffingRequest,
}) {
  const { data: dnrData } = useQuery(DO_NOT_REHIRE_QUERY, {
    variables: {
      identificationCode: profile.identificationCode,
      organizationId: staffingRequest.organization.id,
      organizationBusinessId: staffingRequest.organizationBusiness.id,
      organizationUnitId: staffingRequest.organizationUnit.id,
    },
  });

  const dnr = dnrData?.doNotRehire;

  if (!dnr) {
    return null;
  }

  const organizationNames = {
    organization: staffingRequest.organization.name,
    organization_business: staffingRequest.organizationBusiness.name,
    organization_unit: staffingRequest.organizationUnit.name,
  };

  return (
    <>
      <Drawer
        visible={visible}
        onClose={onClose}
        width={600}
        title={`Submit ${profile.fullName}`}
        footer={
          <Row gutter={[16, 0]}>
            <Col>
              <Button key="back" onClick={onClose} size="large">
                Cancel
              </Button>
            </Col>
            <Col>
              <PrimaryButton size="large" key="submit" onClick={handleSubmit}>
                Submit Anyway
              </PrimaryButton>
            </Col>
          </Row>
        }
      >
        <Alert
          message="This candidate has been set as ”Do Not Rehire” by this facility."
          type="warning"
          showIcon
          className="do-not-rehire-alert"
        />

        <Row gutter={[0, 8]}>
          <Col span={6}>
            <Text type="secondary">Status</Text>
          </Col>
          <Col>Do Not Rehire</Col>
        </Row>

        <Row gutter={[0, 8]}>
          <Col span={6}>
            <Text type="secondary">Level</Text>
          </Col>
          <Col>
            <Text>
              {LEVEL[dnr.institutionLevel]}{' '}
              {organizationNames[dnr.institutionLevel]}
            </Text>
          </Col>
        </Row>

        <Row gutter={[0, 8]}>
          <Col span={6}>
            <Text type="secondary">From</Text>
          </Col>
          <Col>
            <TimeWithTimeZone date={dnr.updatedAt} variant="short_format" />
          </Col>
        </Row>

        <Row gutter={[0, 8]}>
          <Col span={6}>
            <Text type="secondary">To</Text>
          </Col>
          <Col>
            {dnr.endsAt ? (
              <TimeWithTimeZone date={dnr.endsAt} variant="short_format" />
            ) : (
              '-'
            )}
          </Col>
        </Row>

        <Row gutter={[0, 28]}>
          <Col span={6}>
            <Text type="secondary">Duration</Text>
          </Col>
          <Col>{humanize(dnr.expiration)}</Col>
        </Row>

        {supplierId === dnr.supplier.id && (
          <>
            <Row gutter={[0, 16]}>
              <Text>Do Not Rehire comment</Text>
            </Row>

            <Card style={{ backgroundColor: Colors.steelLightest }}>
              <Row>
                <Text>{`"${dnr.reason}"`}</Text>
              </Row>
              <Row>
                <Text type="secondary">
                  {`${dnr.authorName}, `}
                  <TimeWithTimeZone
                    date={dnr.updatedAt}
                    variant="long_format"
                  />
                </Text>
              </Row>
            </Card>
          </>
        )}
      </Drawer>
      <style jsx>{`
        :global(.do-not-rehire-alert) {
          margin-bottom: 16px;
          width: 100%;
        }
      `}</style>
    </>
  );
}

DoNotRehireDrawer.propTypes = {
  onClose: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  visible: PropTypes.bool.isRequired,
  profile: PropTypes.shape({
    fullName: PropTypes.string.isRequired,
    identificationCode: PropTypes.string.isRequired,
  }).isRequired,
  supplierId: PropTypes.string.isRequired,
  staffingRequest: PropTypes.shape({
    organization: PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }).isRequired,
    organizationBusiness: PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }).isRequired,
    organizationUnit: PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
};
